import { useEffect, useState } from "react";
import { EditableRowList } from "./EditableRowList";
import { commitHandlers, fieldInvalid, rowErrorMessages } from "./row-fields";
import { RowSaveFooter, RowStatusIcon } from "./RowSaveState";
import { fieldClass } from "./fields";
import type { CatalogEditor, EditorRow } from "./useCatalogEditor";

type FieldRow = EditorRow<"customFields">;

export type CustomFieldsEditorProps = {
  editor: CatalogEditor;
  onRemove: (row: FieldRow) => void;
  disabled?: boolean;
  /** Desktop: order by dragging instead of the arrows. */
  sortable?: boolean;
};

/** Options are entered comma-separated; blanks are dropped rather than stored. */
function parseOptions(raw: string): string[] {
  return raw
    .split(",")
    .map((o) => o.trim())
    .filter((o) => o.length > 0);
}

function sameOptions(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/**
 * Holds the raw comma-separated text rather than deriving it from the parsed
 * array on every render. Deriving it would drop the comma the moment you type
 * it — `"Matutino, "` parses to `["Matutino"]`, which joins back to
 * `"Matutino"` — making a second option impossible to enter.
 *
 * It resyncs only when the array changes from outside this input (undo, row
 * reorder), detected by the parsed text no longer matching.
 */
function OptionsInput({
  options,
  onChange,
  onBlur,
  onKeyDown,
  invalid,
  disabled,
  ariaLabel,
}: {
  options: string[];
  onChange: (options: string[]) => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  invalid: boolean;
  disabled?: boolean;
  ariaLabel: string;
}): React.JSX.Element {
  const [raw, setRaw] = useState(() => options.join(", "));
  const drifted = !sameOptions(parseOptions(raw), options);

  useEffect(() => {
    if (drifted) setRaw(options.join(", "));
    // `drifted` already encodes the comparison against `options`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drifted]);

  return (
    <input
      className={`${fieldClass({ invalid })} min-w-[170px] flex-1`}
      value={raw}
      disabled={disabled}
      placeholder="Matutino, Vespertino"
      aria-label={ariaLabel}
      onChange={(e) => {
        setRaw(e.target.value);
        onChange(parseOptions(e.target.value));
      }}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    />
  );
}

export function CustomFieldsEditor({
  editor,
  onRemove,
  disabled,
  sortable,
}: CustomFieldsEditorProps): React.JSX.Element {
  return (
    <EditableRowList<FieldRow>
      title="Datos a recabar"
      hint="Lo que el agente intenta capturar además de nombre, teléfono y correo. El nombre interno se usa en la base de datos; la etiqueta es lo que ve tu equipo."
      items={editor.rows.customFields}
      getKey={(row) => row.key}
      onAdd={() =>
        editor.add("customFields", { name: "", label: "", fieldType: "text", options: null })
      }
      onRemove={onRemove}
      onReorder={(rows) => editor.reorder("customFields", rows.map((r) => r.key))}
      canMove={(row) => row.id !== null}
      addLabel="Agregar campo"
      emptyLabel="Aún no hay campos adicionales."
      rowName={(row, i) =>
        row.current.name.trim() ? `el campo ${row.current.name}` : `el campo ${i + 1}`
      }
      rowErrors={(row) => rowErrorMessages(editor, "customFields", row.key)}
      renderRowStatus={(row) => <RowStatusIcon status={row.status} />}
      renderRowFooter={(row) => <RowSaveFooter editor={editor} list="customFields" row={row} />}
      disabled={disabled}
      sortable={sortable}
      renderRow={(row, index) => {
        const commit = commitHandlers(editor, "customFields", row.key);
        return (
          <>
            <input
              className={`${fieldClass({
                mono: true,
                invalid: fieldInvalid(editor, "customFields", row.key, "name"),
              })} w-[130px] shrink-0`}
              value={row.current.name}
              disabled={disabled}
              placeholder="turno"
              aria-label={`Nombre interno del campo ${index + 1}`}
              onChange={(e) =>
                editor.edit("customFields", row.key, { name: e.target.value }, "debounce")
              }
              {...commit}
            />
            <input
              className={`${fieldClass({
                invalid: fieldInvalid(editor, "customFields", row.key, "label"),
              })} min-w-[150px] flex-1`}
              value={row.current.label}
              disabled={disabled}
              placeholder="Turno de preferencia"
              aria-label={`Etiqueta del campo ${index + 1}`}
              onChange={(e) =>
                editor.edit("customFields", row.key, { label: e.target.value }, "debounce")
              }
              {...commit}
            />
            <select
              className={`${fieldClass()} w-[128px] shrink-0`}
              value={row.current.fieldType}
              disabled={disabled}
              aria-label={`Tipo del campo ${index + 1}`}
              onChange={(e) => {
                // Switching to text clears the options (a text field carrying
                // options is a half-finished switch the schema rejects) and
                // saves at once. Switching to «Opciones» waits for the options:
                // a select with none cannot be saved, and the pair travels
                // together.
                const fieldType = e.target.value === "select" ? "select" : "text";
                editor.edit(
                  "customFields",
                  row.key,
                  { fieldType, options: fieldType === "select" ? [] : null },
                  fieldType === "text" ? "now" : "debounce",
                );
              }}
            >
              <option value="select">Opciones</option>
              <option value="text">Texto libre</option>
            </select>
            {row.current.fieldType === "select" && (
              <OptionsInput
                options={row.current.options ?? []}
                onChange={(options) =>
                  editor.edit("customFields", row.key, { options }, "debounce")
                }
                onBlur={commit.onBlur}
                onKeyDown={commit.onKeyDown}
                invalid={fieldInvalid(editor, "customFields", row.key, "options")}
                disabled={disabled}
                ariaLabel={`Opciones del campo ${index + 1}, separadas por comas`}
              />
            )}
          </>
        );
      }}
    />
  );
}
